import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Content, PageBar } from '../components/Layout'
import { api, ApiError } from '../lib/api'

interface Member {
  id: number
  email: string
  name: string
  role: string
  active: boolean
  has_password: boolean
  last_login_at: string | null
}

interface Imported {
  added: number
  skipped: number
  errors: string[]
  already_present: number
  message: string
}

interface Issued {
  member: Member
  password: string
  note: string
}

const ROLES = [
  { value: 'engineer', label: 'Engineer', hint: 'Runs sessions. Sees enquiries, bookings, invoices.' },
  { value: 'owner', label: 'Owner', hint: 'Supervises. Same visibility, no team management.' },
  { value: 'admin', label: 'Admin', hint: 'Can add and remove people. Only give this to yourself.' },
]

function when(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function Team() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('engineer')
  // Shown once, in the page, because the API returns it once and never again.
  const [issued, setIssued] = useState<Issued | null>(null)
  const [imported, setImported] = useState<Imported | null>(null)
  const [replace, setReplace] = useState(false)

  const team = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<Member[]>('/team'),
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] })

  const add = useMutation({
    mutationFn: () => api.post<Issued>('/team', { email, name, role }),
    onSuccess: (data) => {
      setIssued(data)
      setEmail('')
      setName('')
      void refresh()
    },
  })

  const reissue = useMutation({
    mutationFn: (id: number) => api.post<Issued>(`/team/${id}/password`),
    onSuccess: (data) => {
      setIssued(data)
      void refresh()
    },
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      // Not api.post: that sets Content-Type to JSON, and multipart needs the browser
      // to generate its own boundary.
      const response = await fetch(`/api/team/import?replace=${replace}`, {
        method: 'POST',
        body: form,
      })
      if (!response.ok) throw new ApiError(await response.text(), response.status)
      return (await response.json()) as Imported
    },
    onSuccess: (data) => setImported(data),
  })

  const setActive = useMutation({
    mutationFn: (vars: { id: number; active: boolean }) =>
      api.patch<Member>(`/team/${vars.id}`, { active: vars.active }),
    onSuccess: () => void refresh(),
  })

  if (team.error instanceof ApiError && team.error.status === 403) {
    return (
      <>
        <PageBar title="Team" />
        <Content>
          <div className="alarm">
            Admins only. Your account can sign in, but not manage who else can.
          </div>
        </Content>
      </>
    )
  }

  return (
    <>
      <PageBar title="Team" />
      <Content>
        {issued && (
          <div className="issued">
            <div className="issued-head">
              New password for{' '}
              <b>{issued.member.name ? `${issued.member.name} — ` : ''}{issued.member.email}</b>
              <span className="issued-role"> · {issued.member.role}</span>
            </div>
            <code className="issued-pw">{issued.password}</code>
            <p className="issued-note">{issued.note}</p>
            <div className="issued-actions">
              <button
                type="button"
                className="btn"
                onClick={() => void navigator.clipboard?.writeText(issued.password)}
              >
                Copy
              </button>
              <button type="button" className="tab" onClick={() => setIssued(null)}>
                Done
              </button>
            </div>
          </div>
        )}

        <h2 className="sec">Who can sign in</h2>
        <div className="panel">
          <table className="cards">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Password</th>
                <th>Last signed in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(team.data ?? []).map((m) => (
                <tr key={m.id} className={m.active ? '' : 'rowmuted'}>
                  <td>{m.name || <span className="muted">—</span>}</td>
                  <td>{m.email}</td>
                  <td data-label="Role">{m.role}</td>
                  <td data-label="Password">
                    {m.has_password ? (
                      <span className="badge b-client">set</span>
                    ) : (
                      <span className="badge b-needs_triage">link only</span>
                    )}
                  </td>
                  <td className="muted" data-label="Signed in">{when(m.last_login_at)}</td>
                  <td className="rowactions">
                    <button
                      type="button"
                      className="tab"
                      disabled={reissue.isPending}
                      onClick={() => {
                        // Naming the person in the confirm is the fix for the real
                        // failure here: the rows look alike, the password appears in a
                        // banner at the top of the page, and a mis-click silently
                        // issues someone else's credential.
                        const who = m.name ? `${m.name} (${m.email})` : m.email
                        if (window.confirm(`Issue a new password for ${who}?\n\nTheir current one stops working immediately.`)) {
                          reissue.mutate(m.id)
                        }
                      }}
                    >
                      New password
                    </button>
                    <button
                      type="button"
                      className={m.active ? 'tab danger' : 'tab'}
                      disabled={setActive.isPending}
                      onClick={() => {
                        const who = m.name || m.email
                        if (!m.active || window.confirm(`Stop ${who} signing in?`)) {
                          setActive.mutate({ id: m.id, active: !m.active })
                        }
                      }}
                    >
                      {m.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
              {team.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Nobody yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="sec">Import history</h2>
        <div className="panel pad">
          <p className="hint" style={{ marginTop: 0 }}>
            The Ninja Forms CSV export. Every row is filed as <b>archived</b> — we have no
            record of whether these were ever replied to, and showing them as waiting would
            put a wrong number on the dashboard.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={upload.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) upload.mutate(file)
              e.target.value = ''
            }}
          />
          <label className="checkline">
            <input
              type="checkbox"
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
            />
            Replace what is already imported
          </label>
          {upload.isPending && <p className="hint">Importing…</p>}
          {imported && (
            <div className={imported.added ? 'issued' : 'alarm'} style={{ marginTop: 14 }}>
              <b>{imported.message}</b>
              {imported.errors.length > 0 && (
                <ul className="labdetail">
                  {imported.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {upload.error && <span className="v-alert">Upload failed. Is it the right file?</span>}
        </div>

        <h2 className="sec">Add someone</h2>
        <div className="panel pad">
          <form
            className="grid3"
            onSubmit={(event) => {
              event.preventDefault()
              add.mutate()
            }}
          >
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="them@oldstreetstudios.com"
                required
              />
            </label>
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Luis" />
            </label>
            <label>
              Role
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </form>
          <p className="hint">{ROLES.find((r) => r.value === role)?.hint}</p>
          <button className="btn" type="button" disabled={!email || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Creating…' : 'Create and issue a password'}
          </button>
          {add.error instanceof ApiError && add.error.status === 409 && (
            <span className="v-alert">That email already has an account.</span>
          )}
          <p className="hint">
            The password is generated here and shown once. Nobody chooses their own and nobody
            can change it — if someone forgets it, issue a new one.
          </p>
        </div>
      </Content>
    </>
  )
}
